/*
 * Was nur am Bedienfeld erscheint (Patrick, 18.10.2026): die Web-HMI zeigt
 * alles, was die lokale HMI zeigt, mit einer Ausnahme. „Benutzer
 * hinzufügen“ mit dem QR-Code gibt es nur am Panel, nie in der Web-HMI und
 * nie in der App. Markiert wird mit `localOnly: true` an einem Baustein oder
 * einer Seite; der Baustein `user-invite` ist von Natur aus lokal.
 *
 * Darstellung, keine Zugriffskontrolle: eine Einladung erzeugt der Server
 * nur für das Gerätezertifikat des Bedienfelds oder für Systemadmins.
 */

import type { HmiScreen, WidgetDefinition } from './widgets';

/** Kinds, die immer nur lokal erscheinen, unabhängig von der Markierung. */
export const LOCAL_ONLY_KINDS: ReadonlySet<string> = new Set(['user-invite']);

export function isLocalOnlyWidget(w: Pick<WidgetDefinition, 'kind' | 'localOnly'>): boolean {
  return w.localOnly === true || LOCAL_ONLY_KINDS.has(w.kind);
}

export function isLocalOnlyScreen(s: Pick<HmiScreen, 'localOnly'>): boolean {
  return s.localOnly === true;
}

interface Body {
  widgets: WidgetDefinition[];
  screens: HmiScreen[];
}

/**
 * Projekt für die Web-HMI: lokale Seiten und Bausteine entfernt, Verweise
 * darauf aus den übrigen Seiten und aus Navigationsleisten gestrichen.
 * Gibt eine Kopie zurück, das Original bleibt unverändert.
 */
export function stripLocalOnly<T extends Body>(body: T): T {
  const byId = new Map(body.widgets.map((w) => [w.id, w]));
  // Eine Seite, auf der nur Lokales steht (etwa „Benutzer“ mit dem QR-Code),
  // gilt auch ohne Markierung als lokal; sonst bliebe eine leere Seite stehen.
  const onlyLocal = (s: HmiScreen) => {
    const own = s.widgetIds.map((id) => byId.get(id)).filter((w): w is WidgetDefinition => !!w && w.kind !== 'nav-bar');
    return own.length > 0 && own.every(isLocalOnlyWidget);
  };
  const keptScreens = body.screens.filter((s) => !isLocalOnlyScreen(s) && !onlyLocal(s));
  const keptScreenIds = new Set(keptScreens.map((s) => s.id));
  // Ein Knopf, der nur auf eine lokale Seite springt, fuehrt in der Web-HMI ins Leere.
  const leadsNowhere = (w: WidgetDefinition) =>
    w.kind === 'action-button'
    && w.actions.length > 0
    && w.actions.every((a) => a.type === 'navigate' && !!a.screenId && !keptScreenIds.has(a.screenId));
  const droppedWidgets = new Set(body.widgets.filter((w) => isLocalOnlyWidget(w) || leadsNowhere(w)).map((w) => w.id));
  const screens = keptScreens.map((s) => {
    if (!s.widgetIds.some((id) => droppedWidgets.has(id))) return s;
    return {
      ...s,
      widgetIds: s.widgetIds.filter((id) => !droppedWidgets.has(id)),
      layouts: s.layouts.map((l) => ({
        ...l,
        placements: Object.fromEntries(Object.entries(l.placements).filter(([id]) => !droppedWidgets.has(id))),
      })),
    };
  });
  const widgets = body.widgets
    .filter((w) => !droppedWidgets.has(w.id))
    .map((w): WidgetDefinition => {
      if (w.kind === 'nav-bar' && w.screenIds) return { ...w, screenIds: w.screenIds.filter((id) => keptScreenIds.has(id)) };
      if (w.kind === 'action-button') {
        return { ...w, actions: w.actions.filter((a) => a.type !== 'navigate' || !a.screenId || keptScreenIds.has(a.screenId)) };
      }
      return w;
    });
  return { ...body, widgets, screens };
}
